import { sortBy } from 'lodash';
import moment from 'moment';
import { INTER_MODAL_TYPE_EQUIPMENT_TYPES, QUOTE_CLASSIFICATION, QUOTE_URGENCY } from 'src/constants/common';
import { getSelectBoxOptions } from 'src/utils/CommonFunctions';

import { IAddressForm } from './Modals/quoteDetailModal.interface';

const isBusinessDay = (date: moment.Moment) => {
    const day = date.day();

    return day !== 0 && day !== 6; // Skip weekends
};

export const calculateBusinessDays = (startDate: any, endDateOrBusinessDays: any, options?: { pickupDate?: any; skipStartDate?: boolean }) => {
    let current = moment(moment(startDate).format('YYYY-MM-DD')).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
    });
    let count = 0;

    const pickupDate = options?.pickupDate ? moment(options.pickupDate).startOf('day') : null;
    const skipStartDate = options?.skipStartDate || false;

    if (typeof endDateOrBusinessDays === 'number') {
        let businessDays = endDateOrBusinessDays;

        if (businessDays === 0) {
            return current;
        }

        if (skipStartDate) {
            current.add(1, 'days');
        }

        while (businessDays > 0) {
            if (isBusinessDay(current) && (!pickupDate || !current.isSame(pickupDate, 'day'))) {
                businessDays--;
            }

            if (businessDays > 0) {
                current.add(1, 'days');
            }
        }

        return current;
    }

    if (moment.isMoment(moment(endDateOrBusinessDays))) {
        const end = moment(endDateOrBusinessDays).set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });

        if (skipStartDate) {
            current.add(1, 'days');
        }

        while (
            current.year() < end.year() ||
            (current.year() === end.year() && current.month() < end.month()) ||
            (current.year() === end.year() && current.month() === end.month() && current.date() <= end.date())
        ) {
            if (isBusinessDay(current) && (!pickupDate || !current.isSame(pickupDate, 'day'))) {
                count++;
            }
            current.add(1, 'days');
        }

        return count === 0 ? 1 : count;
    }

    return current;
};

export const setTeamMemberOptions = (team: any, userSelected: any, teamMembers: any[]) => {
    teamMembers = sortBy(teamMembers, ['firstName', 'lastName']);

    if (team === 'all' && userSelected != 'all') {
        const selectedTeamMemberIndex = teamMembers.findIndex((member: any, index: number) => member.id == userSelected && index > 4);

        if (selectedTeamMemberIndex !== -1) {
            const selectedTeamMember = teamMembers[selectedTeamMemberIndex];
            teamMembers = teamMembers.slice(0, 4);
            teamMembers.unshift(selectedTeamMember);
        } else {
            teamMembers = teamMembers.slice(0, 5);
        }
    } else {
        teamMembers = teamMembers.slice(0, 5);
    }

    return getSelectBoxOptions(teamMembers, 'id', 'firstName', true, 'imageUrl', 'image', undefined, 'lastName', true, null, 'email');
};

export const addActiveQuoteTag = (carriers: any[]) => {
    if (!carriers?.length) return carriers;

    const publicCarriers = carriers;

    const publicCarriersSortedByPrice = [...publicCarriers].sort((a, b) => {
        const chargeA = a?.finalChargeUSD ?? a?.finalCharge ?? 0;
        const chargeB = b?.finalChargeUSD ?? b?.finalCharge ?? 0;

        return chargeA - chargeB;
    });

    const cheapestCarriers = publicCarriersSortedByPrice
        .filter((carrier) => (publicCarriersSortedByPrice[0]?.finalChargeUSD ?? publicCarriersSortedByPrice[0]?.finalCharge) === (carrier?.finalChargeUSD ?? carrier?.finalCharge))
        .sort((a, b) => {
            const timeA = a.transitTime ?? 0;
            const timeB = b.transitTime ?? 0;

            return timeA - timeB;
        });

    const cheapestCarrierIds: number[] = [];

    cheapestCarriers.forEach((carrier: any) => {
        if (cheapestCarriers[0]?.transitTime === carrier?.transitTime && cheapestCarrierIds.length === 0) {
            cheapestCarrierIds.push(carrier.id);
        }
    });

    const fastestCarriers = publicCarriersSortedByPrice
        .filter((carrier) => !cheapestCarrierIds.includes(carrier.id))
        .sort((a, b) => {
            const timeA = a.transitTime ?? 0;
            const timeB = b.transitTime ?? 0;

            return timeA - timeB;
        });

    const expressCarrierIds: number[] = [];

    fastestCarriers.forEach((carrier: any) => {
        if ((fastestCarriers[0]?.finalChargeUSD ?? fastestCarriers[0]?.finalCharge) === (carrier?.finalChargeUSD ?? carrier?.finalCharge) && expressCarrierIds.length === 0) {
            expressCarrierIds.push(carrier.id);
        }
    });

    return carriers.map((carrier) => ({
        ...carrier,
        tag: cheapestCarrierIds.includes(carrier.id) ? 'Cheapest' : expressCarrierIds.includes(carrier.id) ? 'Fastest' : '-',
    }));
};

export const sortDataByIsQuoted = (data: any) =>
    data.sort((a: any, b: any) => {
        const aIsQuotedFalse = a?.services.some((service: any) => service.isQuoted === false);
        const bIsQuotedFalse = b?.services.some((service: any) => service.isQuoted === false);

        return aIsQuotedFalse === bIsQuotedFalse ? 0 : aIsQuotedFalse ? 1 : -1;
    });

export const createPayloadFromAddressForm = (data: IAddressForm) => {
    const payload = {
        address: {
            urgency: QUOTE_URGENCY.LIVE,
            pickupDate: moment.utc(data.pickupDate).format('YYYY-MM-DD 00:00:00'),
            pickupSensitivity: data.pickupSensitivity,
            pickupTimeFrame: data.pickupTimeFrame,
            pickupEndDate: data.pickupEndDate ? moment.utc(data.pickupEndDate).format('YYYY-MM-DD 00:00:00') : null,
            pickupWindowType: data.pickupWindowType,
            pickupStartTime: data.pickupStartTime || null,
            pickupEndTime: data.pickupEndTime || null,
            deliveryDate: data.deliveryDate ? moment.utc(data.deliveryDate).format('YYYY-MM-DD 00:00:00') : null,
            deliverySensitivity: data.deliverySensitivity,
            deliveryEndDate: data.deliveryEndDate ? moment.utc(data.deliveryEndDate).format('YYYY-MM-DD 00:00:00') : null,
            deliveryWindowType: data.deliveryWindowType,
            deliveryStartTime: data.deliveryStartTime || null,
            deliveryEndTime: data.deliveryEndTime || null,
            shipperId: data.shipperId,
            consigneeId: data.consigneeId,

            // Shipper
            shipperCompanyName: data.shipperCompanyName,
            shipperFullAddress: data.shipperFullAddress,
            shipperAddress1: data.shipperAddress1,
            shipperStreetNumber: data.shipperStreetNumber,
            shipperCity: data.shipperCity,
            shipperState: data.shipperState,
            shipperCountry: data.shipperCountry,
            shipperPostal: data.shipperPostal,
            shipperLatitude: data.shipperLatitude,
            shipperLongitude: data.shipperLongitude,
            shipperStateCode: data.shipperStateCode,
            shipperCountryCode: data.shipperCountryCode,

            // Consignee
            consigneeFullAddress: data.consigneeFullAddress,
            consigneeAddress1: data.consigneeAddress1,
            consigneeStreetNumber: data.consigneeStreetNumber,
            consigneeCity: data.consigneeCity,
            consigneeState: data.consigneeState,
            consigneeCountry: data.consigneeCountry,
            consigneePostal: data.consigneePostal,
            consigneeLatitude: data.consigneeLatitude,
            consigneeLongitude: data.consigneeLongitude,
            consigneeStateCode: data.consigneeStateCode,
            consigneeCountryCode: data.consigneeCountryCode,
            consigneeCompanyName: data.consigneeCompanyName,

            // Classification and equipment types
            classification: data.classification,
            linearFootage: data.linearFootage,
            totalWeight: data.totalWeight,

            // Insurance and other details — clear fields when insurance is disabled,
            // matching the reference implementation to prevent stale "0" values being saved to DB
            insuranceCurrencyType: data.insuranceCurrencyType,
            insuranceCommercialValue: data.isLoadInsuranceEnabled ? data.insuranceCommercialValue : null,
            insuranceCommercialValueUSD: data.isLoadInsuranceEnabled ? data.insuranceCommercialValueUSD : null,
            insuranceCommodity: data.insuranceCommodity?.length > 0 ? data.insuranceCommodity.join('|') : null,
            isLoadInsuranceEnabled: data.isLoadInsuranceEnabled,
            isFromQuoteRequest: true,
            isFromEditQuoteDetail: true,

            primaryEquipmentType: data.primaryEquipmentType,
            primaryEquipmentTypeFullName: data.primaryEquipmentTypeFullName,
            secondaryEquipmentTypes: data.secondaryEquipmentTypes?.join(','),
            secondaryEquipmentTypeFullNames: data.secondaryEquipmentTypeFullNames?.join(','),
            interModalType: INTER_MODAL_TYPE_EQUIPMENT_TYPES.some((type) => data.primaryEquipmentTypeFullName?.toLowerCase().includes(type)) ? data.interModalType : null,
            specialNotes: data.specialNotes || null,
        },

        services: {
            serviceId: data.serviceId?.join(','),
            //limited access
            limitedAccess: data.limitedAccess,
            ...(data.limitedAccess && {
                limitedAccessType: data.limitedAccessType,
                limitedAccessOther: data.limitedAccessOther,
            }),

            // inbond
            inbond: data.inbond,
            ...(data.inbond && {
                inbondType: data.inbondType,
                inbondName: data.inbondName,
                inbondAddress: data.inbondAddress,
                inbondContactMethod: data.inbondContactMethod,
                inbondContactValue: data.inbondContactValue,
            }),

            // dangerous goods
            dangerousGoods: data.dangerousGoods,
            ...(data.dangerousGoods && {
                dangerGoodsType: data.dangerGoodsType,
                unitedNationsNumber: data.unitedNationsNumber,
                packagingGroup: data.packagingGroup,
                goodsClass: data.goodsClass,
                description: data.description,
                emergencyContactName: data.emergencyContactName,
                emergencyContactNumber: data.emergencyContactNumber,
                emergencyContactExt: data.emergencyContactExt,
                emergencyEapnbr: data.emergencyEapnbr,
            }),
        },

        dimensions: (() => {
            if (!data.dimensions || data.dimensions.length === 0 || data.classification === QUOTE_CLASSIFICATION?.FTL) {
                return [];
            }

            if (data.dimensions.length === 1) {
                const dimension = data.dimensions[0];
                const hasValidValue =
                    (dimension.weight && dimension.weight >= 1) ||
                    (dimension.itemLength && dimension.itemLength >= 1) ||
                    (dimension.width && dimension.width >= 1) ||
                    (dimension.height && dimension.height >= 1) ||
                    (dimension.totalWeight && dimension.totalWeight >= 1);

                if (!hasValidValue) {
                    return [];
                }
            }

            return data.dimensions.map((dimension) => ({
                id: dimension.id,
                handlingUnit: dimension.handlingUnit,
                handlingUnitNo: dimension.handlingUnitNo,
                weight: dimension.weight,
                itemLength: dimension.itemLength,
                width: dimension.width,
                height: dimension.height,
                totalWeight: dimension.totalWeight,
                isStack: dimension.isStack,
                weightMeasurement: data.weightMeasurement,
                lengthMeasurement: data.lengthMeasurement,
                commodity: dimension?.commodity || null,
                freightClass: dimension.freightClass,
                itemClassForDayross: dimension.itemClassForDayross,
                // Task 37 (client decision) — multi-stop item re-routing in Edit Quote, matching
                // the Add Quote wizard's Dimensions step. null/absent on classic quotes and on any
                // stop-count-2 caller of this shared payload builder — ignored there.
                pickupStopPosition: dimension?.pickupStopPosition ?? null,
                dropStopPosition: dimension?.dropStopPosition ?? null,
            }));
        })(),
    };

    return payload;
};
